
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import {
  DEFAULT_UNIT_TYPES,
  UnitNumber,
  UnitInputValue,
  UnitOptions,
  UnitType
} from "@/types/unit";
import { RangeOptions } from "@/types/controls";

import { isNumber } from "lodash";

import UnitDropdown from "@/components/Reusable/CustomInputs/UnitInput/UnitDropdown.vue";
import Slider from "@/components/Reusable/Slider.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import { getStyleForUnit, parseNumberAndUnitFromRawValue } from "@/util/unit";

@Component({
  components: { NumberInput, TextInput, Dropdown, UnitDropdown, Slider }
})
export default class UnitInput extends Vue {
  @Prop({
    default: null
  })
  value!: UnitInputValue;

  @Prop({ default: null })
  unitOptions!: UnitOptions | null;

  @Prop({ default: null })
  rangeOptions!: RangeOptions | null;

  @Prop({ default: false })
  disabled!: boolean;

  rawValue: UnitNumber = "";
  dirty = false;

  get unitTypes(): UnitType[] {
    if (this.unitOptions?.types?.length) {
      return this.unitOptions?.types;
    }

    return DEFAULT_UNIT_TYPES;
  }

  get defaultType(): UnitType {
    const defaultFirstType = this.unitTypes.length ? this.unitTypes[0] : "px";
    return this.unitOptions?.defaultType || defaultFirstType;
  }

  get shouldShowRangeSlider() {
    if (
      typeof this.rangeOptions?.min === "number" &&
      typeof this.rangeOptions?.max === "number" &&
      this.rangeOptions?.disableSlider !== true
    ) {
      // Only show range slider for the default unit. #1623
      return !this.value || this.value?.unit === this.defaultType;
    }

    return false;
  }

  get step() {
    return this.rangeOptions?.step || 1;
  }

  get internalNumber(): UnitNumber {
    return this.value?.number;
  }

  get internalUnit(): UnitType {
    return this.value?.unit || this.defaultType;
  }

  get isCustomCSS() {
    return this.internalUnit === "custom";
  }

  get canIncrement() {
    return !["custom", "auto"].includes(this.internalUnit);
  }

  updateNumber(newNum: UnitNumber) {
    this.update(newNum, this.internalUnit);
  }

  updateUnit(unit: UnitType) {
    if (unit === "auto") {
      // Clear number if user selects `auto`
      this.update(null, unit);
      return;
    }

    this.update(this.internalNumber, unit);
  }

  update(number: UnitNumber, unit: UnitType | null) {
    if ((!unit || unit === this.defaultType) && !number && number !== 0) {
      // Clear the whole input if no unit or number are present.
      this.$emit("input", null);
      return;
    }

    const style = getStyleForUnit(number, unit);
    this.$emit("input", { number, unit, style });
  }

  increment(event: KeyboardEvent) {
    if (!this.canIncrement) return;

    this.dirty = false;
    const num = (this.internalNumber || 0) as number;
    const modifier = event.shiftKey ? 10 : 1;

    this.updateNumber(num + modifier);
  }

  decrement(event: KeyboardEvent) {
    if (!this.canIncrement) return;

    this.dirty = false;
    const num = (this.internalNumber || 0) as number;
    const modifier = event.shiftKey ? 10 : 1;

    this.updateNumber(num - modifier);
  }

  @Watch("value.number", { immediate: true })
  onValueChange() {
    if (this.dirty) {
      this.dirty = false;
      return;
    }

    this.rawValue = isNumber(this.value?.number)
      ? `${this.value?.number}`
      : this.value?.number;
  }

  updateRawValue(newValue: string | null) {
    this.rawValue = newValue;

    // Disable unit switching for custom values
    if (this.internalUnit === "custom") {
      this.updateNumber(this.rawValue);
      return;
    }

    const [parsedNumber, parsedUnit, numMinusUnit] =
      parseNumberAndUnitFromRawValue(this.rawValue, this.unitTypes);

    const maybeNewUnit = parsedUnit || this.internalUnit;

    if (numMinusUnit) {
      this.rawValue = `${numMinusUnit}`;
      this.dirty = true;
    }

    this.update(parsedNumber, maybeNewUnit);
  }
}
